import { Action, createReducer, on } from '@ngrx/store';

import { Principal } from '../services/object.service';

import {
  resetPrincipalsState,
  loadCompanyPrincipals,
  setOriginalPrincipals,
  clearTemporaryChanges,
  addTemporaryPrincipal,
  clearSelectedPrincipals,
  removeTemporaryPrincipal,
  togglePrincipalSelection,
  toggleAllPrincipalSelection,
  initializeSelectedPrincipals,
  loadMissingPrincipalsSuccess,
  loadCompanyPrincipalsSuccess,
  setCurrentCompany,
  initializeCompanyWithPrincipals,
  snapshotTemporaryChanges,
  restoreTemporaryChanges,
  commitTemporaryChanges,
} from '../actions/principals.actions';

function mergeById(existing: Principal[], incoming: Principal[]): Principal[] {
  const map = new Map(existing.map((p) => [p.id, p]));
  for (const p of incoming) {
    map.set(p.id, p);
  }
  return Array.from(map.values());
}

// * helper function to compute selectedPrincipals from Original + Temporary changes
function computeSelectedPrincipals(
  originalPrincipals: Map<string, Principal>,
  temporaryChanges: { added: Map<string, Principal>; removed: Set<string> },
): Map<string, Principal> {
  const result = new Map<string, Principal>();

  // * start with original principals
  originalPrincipals.forEach((principal, id) => {
    if (!temporaryChanges.removed.has(id)) {
      result.set(id, principal);
    }
  });

  // * add temporary additions
  temporaryChanges.added.forEach((principal, id) => {
    result.set(id, principal);
  });

  return result;
}

export interface PrincipalState {
  principals: Array<Principal>; // ! Company Users list from API
  loading: boolean;
  total: number;
  itemsPerPage: number;
  page: number;
  error: string | null;
  selectedPrincipals: Map<string, Principal>; // ! computed: original + temporary changes

  // * Track original vs temporary state
  originalPrincipals: Map<string, Principal>; // ! Backend source of truth
  temporaryChanges: {
    added: Map<string, Principal>; // ! Users added in Popup
    removed: Set<string>; // ! Users removed in Popup
  };

  // * Track current company context for automatic cleanup
  currentCompanyId: string | null | undefined;

  // * Track if state was just cleared due to company change
  companyJustChanged: boolean;

  // * Track snapshot of temporary changes
  snapshot: {
    added: Map<string, Principal>;
    removed: Set<string>;
  } | null;
}

export const initialState: PrincipalState = {
  principals: [],
  loading: false,
  total: 0,
  itemsPerPage: 30,
  page: 1,
  error: null,
  selectedPrincipals: new Map<string, Principal>(),

  // * Initialize new state fields
  originalPrincipals: new Map<string, Principal>(),
  temporaryChanges: {
    added: new Map<string, Principal>(),
    removed: new Set<string>(),
  },

  // * Initialize current company context
  currentCompanyId: undefined,
  companyJustChanged: false,

  // * Initialize snapshot
  snapshot: null,
};

const reducer = createReducer(
  initialState,

  on(loadCompanyPrincipals, (state) => ({
    ...state,
    loading: true,
    error: null,
    principals: [],
  })),

  on(
    loadCompanyPrincipalsSuccess,
    (state, { principals, totalItems, page }) => ({
      ...state,
      loading: false,
      total: totalItems,
      page: page,
      principals,
    }),
  ),

  // * Toggle Selection - now uses temporary state system
  on(togglePrincipalSelection, (state, { principal, singleSelection }) => {
    let newTemporaryChanges = {
      added: new Map(state.temporaryChanges.added),
      removed: new Set(state.temporaryChanges.removed),
    };

    if (singleSelection) {
      // 🔹 Reset everything
      newTemporaryChanges = {
        added: new Map([[principal.id, principal]]),
        removed: new Set(),
      };

      // If the principal was originally selected, don't keep it in `added`
      if (state.originalPrincipals.has(principal.id)) {
        newTemporaryChanges.added.delete(principal.id);
      }

      // If any other original principals were selected, mark them as removed
      state.originalPrincipals.forEach((_, id) => {
        if (id !== principal.id) {
          newTemporaryChanges.removed.add(id);
        }
      });
    } else {
      const isCurrentlySelected = state.selectedPrincipals.has(principal.id);

      if (isCurrentlySelected) {
        newTemporaryChanges.added.delete(principal.id);

        if (state.originalPrincipals.has(principal.id)) {
          newTemporaryChanges.removed.add(principal.id);
        }
      } else {
        newTemporaryChanges.added.set(principal.id, principal);
        newTemporaryChanges.removed.delete(principal.id);
      }
    }

    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      newTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: newTemporaryChanges,
      selectedPrincipals,
    };
  }),

  // * Initialize Selection - maintains backward compatibility
  on(initializeSelectedPrincipals, (state, { principals }) => {
    const map = new Map(state.selectedPrincipals);
    for (const p of principals) {
      map.set(p.id, p);
    }
    return {
      ...state,
      selectedPrincipals: map,
    };
  }),

  on(loadMissingPrincipalsSuccess, (state, { principals }) => ({
    ...state,
    principals: mergeById(state.principals, principals),
  })),

  on(toggleAllPrincipalSelection, (state, { principals, selected }) => {
    let newTemporaryChanges = {
      added: new Map(state.temporaryChanges.added),
      removed: new Set(state.temporaryChanges.removed),
    };

    if (selected) {
      // * add all current-page Principals to temporary selection
      principals.forEach((p) => {
        newTemporaryChanges.added.set(p.id, p);
        newTemporaryChanges.removed.delete(p.id);
      });
    } else {
      // * remove all current-page Principals from temporary selection
      principals.forEach((p) => {
        newTemporaryChanges.added.delete(p.id);

        // * add to temporary removals if it was originally selected
        if (state.originalPrincipals.has(p.id)) {
          newTemporaryChanges.removed.add(p.id);
        }
      });
    }

    // * recompute selectedPrincipals
    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      newTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: newTemporaryChanges,
      selectedPrincipals,
    };
  }),

  on(resetPrincipalsState, (state) => ({
    ...initialState,
  })),

  on(clearSelectedPrincipals, (state) => {
    // Clear temporary additions and mark all original principals as removed
    const newTemporaryChanges = {
      added: new Map<string, Principal>(), // Clear all temporary additions
      removed: new Set<string>(), // Add all original principals to removed
    };

    // * add all original Principals to the removed set
    state.originalPrincipals.forEach((principal, id) => {
      newTemporaryChanges.removed.add(id);
    });

    // * recompute selected Principals - should be empty after clearing
    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      newTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: newTemporaryChanges,
      selectedPrincipals,
    };
  }),

  // * Set original principals from backend (source of truth)
  on(setOriginalPrincipals, (state, { principals }) => {
    // If company just changed, don't restore old principals - keep them empty
    if (state.companyJustChanged) {
      return {
        ...state,
        companyJustChanged: false, // Reset the flag
      };
    }

    const originalMap = new Map<string, Principal>();
    for (const p of principals) {
      originalMap.set(p.id, p);
    }

    // * Recompute selectedPrincipals with new original data
    const selectedPrincipals = computeSelectedPrincipals(
      originalMap,
      state.temporaryChanges,
    );

    return {
      ...state,
      originalPrincipals: originalMap,
      selectedPrincipals,
    };
  }),

  // * Add temporary principal (user added in popup)
  on(addTemporaryPrincipal, (state, { principal }) => {
    const newTemporaryChanges = {
      added: new Map(state.temporaryChanges.added),
      removed: new Set(state.temporaryChanges.removed),
    };

    newTemporaryChanges.added.set(principal.id, principal);
    newTemporaryChanges.removed.delete(principal.id);

    // * Recompute selectedPrincipals
    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      newTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: newTemporaryChanges,
      selectedPrincipals,
    };
  }),

  // * Remove temporary principal (user removed in popup)
  on(removeTemporaryPrincipal, (state, { principalId }) => {
    const newTemporaryChanges = {
      added: new Map(state.temporaryChanges.added),
      removed: new Set(state.temporaryChanges.removed),
    };

    newTemporaryChanges.added.delete(principalId);

    if (state.originalPrincipals.has(principalId)) {
      newTemporaryChanges.removed.add(principalId);
    }

    // * Recompute selectedPrincipals
    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      newTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: newTemporaryChanges,
      selectedPrincipals,
    };
  }),

  // * Clear temporary changes (when main popup closes)
  on(clearTemporaryChanges, (state) => {
    const clearedTemporaryChanges = {
      added: new Map<string, Principal>(),
      removed: new Set<string>(),
    };

    // * recompute selectedPrincipals with only original data
    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      clearedTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: clearedTemporaryChanges,
      selectedPrincipals,
    };
  }),

  // * Set current company and auto-clear temporary changes if company changed
  on(setCurrentCompany, (state, { companyId }) => {
    // * check if company changed
    const normalizedCurrentId = state.currentCompanyId || null;
    const normalizedNewId = companyId || null;

    const companyChanged = normalizedCurrentId !== normalizedNewId;

    if (!companyChanged) {
      return {
        ...state,
        currentCompanyId: companyId,
      };
    }

    // * Company changed - reset original Principals
    const clearedTemporaryChanges = {
      added: new Map<string, Principal>(),
      removed: new Set<string>(),
    };

    const emptyOriginalPrincipals = new Map<string, Principal>();

    const selectedPrincipals = computeSelectedPrincipals(
      emptyOriginalPrincipals,
      clearedTemporaryChanges,
    );

    return {
      ...state,
      currentCompanyId: companyId,
      temporaryChanges: clearedTemporaryChanges,
      originalPrincipals: emptyOriginalPrincipals,
      selectedPrincipals,
      companyJustChanged: true,
    };
  }),

  // * Initialize company with principals (when main popup opens)
  on(initializeCompanyWithPrincipals, (state, { companyId, principals }) => {
    const originalMap = new Map<string, Principal>();
    for (const p of principals) {
      originalMap.set(p.id, p);
    }

    const selectedPrincipals = computeSelectedPrincipals(
      originalMap,
      state.temporaryChanges,
    );

    return {
      ...state,
      currentCompanyId: companyId,
      originalPrincipals: originalMap,
      selectedPrincipals,
      // * No cleanup needed here
      companyJustChanged: false,
    };
  }),

  // * Snapshot temporary changes
  on(snapshotTemporaryChanges, (state) => ({
    ...state,
    snapshot: {
      added: new Map(state.temporaryChanges.added),
      removed: new Set(state.temporaryChanges.removed),
    },
  })),

  // * Restore temporary changes from snapshot
  on(restoreTemporaryChanges, (state) => {
    if (!state.snapshot) {
      return { ...state, snapshot: null };
    }

    const restoredTemporaryChanges = {
      added: new Map(state.snapshot.added),
      removed: new Set(state.snapshot.removed),
    };

    const selectedPrincipals = computeSelectedPrincipals(
      state.originalPrincipals,
      restoredTemporaryChanges,
    );

    return {
      ...state,
      temporaryChanges: restoredTemporaryChanges,
      selectedPrincipals,
      snapshot: null,
    };
  }),

  // * Commit temporary changes to original principals
  on(commitTemporaryChanges, (state) => {
    const newOriginalPrincipals = new Map<string, Principal>();

    // * Copy existing original principals
    state.originalPrincipals.forEach((principal, id) => {
      newOriginalPrincipals.set(id, principal);
    });

    // * Apply temporary additions
    state.temporaryChanges.added.forEach((principal, id) => {
      newOriginalPrincipals.set(id, principal);
    });

    // * Apply temporary removals
    state.temporaryChanges.removed.forEach((id) => {
      newOriginalPrincipals.delete(id);
    });

    const selectedPrincipals = computeSelectedPrincipals(
      newOriginalPrincipals,
      { added: new Map<string, Principal>(), removed: new Set<string>() },
    );

    return {
      ...state,
      originalPrincipals: newOriginalPrincipals,
      temporaryChanges: {
        added: new Map<string, Principal>(),
        removed: new Set<string>(),
      },
      selectedPrincipals,
      snapshot: null,
    };
  }),
);

export function principalReducer(
  state: PrincipalState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
